import { KFCard } from '@kf/ic2fx';
import { Divider, Stack, Typography } from '@mui/material';
import { truncateComments } from 'modules/hiring/utilities';
import React, { useState, useMemo } from 'react';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import CandidateSkillRating from 'shared/components/candidateSkillRating';
import ArrowDownIcon from 'shared/icons/ArrowDownIcon';
import ArrowUpIcon from 'shared/icons/ArrowUpIcon';
import { colors } from 'styles/colors';
import CandidateFeedbackDetailHeader from './candidateFeedbackDetailHeader';
import { ICandidateFeedbackListDetails } from 'modules/hiring/models/candidateFeedbackListDetails.model';
import {
  FeedbackDetailResponseItemsName,
  InterviewFeedbackQuestionType,
} from 'models/enum';
import { areAllRatingsNA } from 'modules/hiring/models/candidateFeedbackOverallRating.model';

export interface IIndividualFeedback {
  feedbackData: ICandidateFeedbackListDetails;
  id: number;
}
const IndividualFeedback = ({ feedbackData, id }: IIndividualFeedback) => {
  const [isShowMore, setShowMore] = useState<boolean>(false);
  const shouldDisplayNoRatingsMessage = useMemo(() => {
    return areAllRatingsNA(feedbackData?.response?.responseItemList);
  }, [feedbackData?.response?.responseItemList]);
  return (
    <KFCard
      key={id}
      id={`feedback-section-${id}`}
      data-testid={`feedback-section-${id}`}
      sx={{
        border: `1px solid ${colors.connector.border}`,
        overflow: 'auto',
      }}
    >
      <CandidateFeedbackDetailHeader data={feedbackData} indexValue={id} />
      <Divider
        sx={{
          mt: 1,
          mb: 2,
          borderColor: colors.border.gainsboroDarkGrey,
        }}
      />
      <Stack direction='row' gap={2.5} flexWrap='wrap'>
        <Stack gap={1.25} sx={{ minWidth: '17.4rem' }}>
          <TypographyWithI18N
            className='kfic2-bodyTextBold'
            i18nKey='skillRatings'
          />
          <Stack
            gap={0.5}
            sx={{
              height: '100%',
              overflow: 'auto',
            }}
          >
            {shouldDisplayNoRatingsMessage ? (
              <Stack
                sx={{
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                <TypographyWithI18N
                  className='kfic2-bodyTextSmall'
                  id='no-ratings'
                  i18nKey='noRatingsProvidedByInterviewer'
                />
              </Stack>
            ) : (
              feedbackData?.response?.responseItemList
                .filter(
                  (item) =>
                    item.questionType ===
                    InterviewFeedbackQuestionType.LIKART_WITH_NA
                )
                .sort((a, b) => a?.label.localeCompare(b?.label))
                .map((skillData, i) => {
                  return (
                    <CandidateSkillRating
                      key={i}
                      name={skillData?.label}
                      rating={skillData?.selectedOption}
                    />
                  );
                })
            )}
          </Stack>
        </Stack>
        <Divider
          orientation='vertical'
          sx={{
            width: '1px',
            height: '12.938rem',
            borderColor: colors.border.gainsboroDarkGrey,
          }}
        />
        <Stack gap={1.25} sx={{ flex: 2.125 }}>
          <TypographyWithI18N
            className='kfic2-bodyTextBold'
            i18nKey='comments'
          />
          {feedbackData?.response?.responseItemList?.map((data, index) => {
            return (
              data?.label === FeedbackDetailResponseItemsName.RECOMMENDHIRE && (
                <React.Fragment key={index}>
                  <Typography
                    className='kfic2-bodyTextSmall'
                    key={index}
                    sx={{ wordWrap: 'break-word' }}
                  >
                    {truncateComments(
                      data?.textComment as string,
                      isShowMore,
                      375
                    )}
                  </Typography>
                  {data?.textComment && data?.textComment?.length > 375 && (
                    <Stack
                      direction='row'
                      gap={1}
                      alignItems='center'
                      justifyContent='center'
                      sx={{
                        mb: '0.625rem',
                        mt: 'auto',
                      }}
                      id={`show-more-less-container-${id}`}
                      data-testid={`show-more-less-container-${id}`}
                      onClick={() => setShowMore(!isShowMore)}
                    >
                      <TypographyWithI18N
                        className='kfic2-bodyTextBold'
                        data-testid={`show-more-less-link-${id}`}
                        i18nKey={isShowMore ? 'showLess' : 'showMore'}
                        sx={{
                          color: `${colors.primaryDark}!important`,
                          cursor: 'pointer',
                        }}
                      />
                      {isShowMore ? (
                        <ArrowUpIcon
                          id={`up-icon-${id}`}
                          data-testid={`up-icon-${id}`}
                          viewBox='0 4 18 8'
                          sx={{
                            width: '1.25rem',
                            height: '1.25rem',
                            cursor: 'pointer',
                          }}
                        />
                      ) : (
                        <ArrowDownIcon
                          id={`down-icon-${id}`}
                          data-testid={`down-icon-${id}`}
                          viewBox='0 0 18 6'
                          sx={{
                            width: '1.25rem',
                            height: '1.25rem',
                            cursor: 'pointer',
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </React.Fragment>
              )
            );
          })}
        </Stack>
      </Stack>
    </KFCard>
  );
};

export default IndividualFeedback;
